import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';

import type { ResearchBoardNotification } from './index';
import styles from './notification.module.css';

type Props = {
  actionUrl?: string;
  actionColor?: string;
  accentColor: string;
  notification: ResearchBoardNotification;
  onDismiss: () => void;
};

export const ResearchBoardNotificationView = ({
  actionUrl,
  actionColor,
  accentColor,
  notification,
  onDismiss,
}: Props) => {
  const isError = notification.type === 'error';
  const notificationColor = isError ? '#c62828' : accentColor;
  const notificationActionColor = actionColor || accentColor;
  const notificationStyle = {
    '--notification-accent': notificationColor,
    '--notification-action': notificationActionColor,
  } as CSSProperties;

  return createPortal(
    <div
      aria-atomic="true"
      aria-live={isError ? 'assertive' : 'polite'}
      className={styles.notification}
      data-testid="research-board-notification"
      // Portals still bubble React events to the save button's ancestors, so
      // a click here would also fire a clickable product card's handler.
      onClick={(event) => event.stopPropagation()}
      role={isError ? 'alert' : 'status'}
      style={notificationStyle}
    >
      <span aria-hidden="true" className={styles.icon}>
        {isError ? (
          <span className={styles.errorMark}>!</span>
        ) : (
          <svg
            aria-hidden="true"
            className={styles.successMark}
            focusable="false"
            viewBox="0 0 24 24"
          >
            <path d="m9.55 18-5.7-5.7 1.4-1.4 4.3 4.3 9.2-9.2 1.4 1.4z" />
          </svg>
        )}
      </span>
      <span className={styles.content}>
        <span className={styles.message}>{notification.message}</span>
        {notification.action && actionUrl && (
          <button
            type="button"
            className={styles.action}
            onClick={() => window.location.assign(actionUrl)}
          >
            {notification.action.label}
          </button>
        )}
      </span>
      <button
        aria-label="Dismiss Saved Products notification"
        className={styles.dismiss}
        onClick={onDismiss}
        type="button"
      >
        ×
      </button>
    </div>,
    document.body
  );
};
